import { apiClient } from "./client";
import type {
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  GetOrganizationResponse,
  GetOrganizationTreeResponse,
  UpdateOrganizationResponse,
  InviteMemberRequest,
  InviteMemberResponse,
  ListMembersResponse,
  ListOrganizationsForUserResponse,
  ListOrganizationsResponse,
  RemoveMemberResponse,
  UpdateMemberRoleRequest,
  UpdateMemberRoleResponse,
  MemberRole,
} from "./types";

// Mirrors OrgService RPCs in console/proto/org.proto.

export async function createOrganization(
  req: CreateOrganizationRequest,
): Promise<CreateOrganizationResponse> {
  const { data } = await apiClient.post<CreateOrganizationResponse>("/organizations", req);
  return data;
}

export async function getOrganization(id: string): Promise<GetOrganizationResponse> {
  const { data } = await apiClient.get<GetOrganizationResponse>(`/organizations/${id}`);
  return data;
}

// Staff only (server refuses everyone else): search organizations by a
// name/slug substring, optionally only children of one parent. The server
// caps the page size at 100.
export async function listOrganizations(params?: {
  query?: string;
  parentOrganizationId?: string;
  limit?: number;
  offset?: number;
}): Promise<ListOrganizationsResponse> {
  const { data } = await apiClient.get<ListOrganizationsResponse>("/admin/organizations", { params });
  return data;
}

// Rename and/or re-slug an organization. OWNER only (enforced server side).
export async function updateOrganization(
  id: string,
  changes: { name?: string; slug?: string },
): Promise<UpdateOrganizationResponse> {
  const { data } = await apiClient.patch<UpdateOrganizationResponse>(`/organizations/${id}`, {
    organizationId: id,
    ...changes,
  });
  return data;
}

// Direct parent and children (names and slugs only). Members only. The tree
// is structure, not access (ADR-014).
export async function getOrganizationTree(id: string): Promise<GetOrganizationTreeResponse> {
  const { data } = await apiClient.get<GetOrganizationTreeResponse>(`/organizations/${id}/tree`);
  return data;
}

// The organizations the given (normally: the currently logged-in) user
// actually belongs to. Backs the real "which org am I in" lookup for a
// logged-in session -- see stores/currentOrg.ts, which replaces the
// dev-only DevAuthPanel as the primary source of the active org.
export async function listOrganizationsForUser(
  userId: string,
): Promise<ListOrganizationsForUserResponse> {
  const { data } = await apiClient.get<ListOrganizationsForUserResponse>(
    `/users/${userId}/organizations`,
  );
  return data;
}

export async function listMembers(
  organizationId: string,
  params?: { limit?: number; offset?: number },
): Promise<ListMembersResponse> {
  const { data } = await apiClient.get<ListMembersResponse>(
    `/organizations/${organizationId}/members`,
    { params },
  );
  return data;
}

export async function inviteMember(req: InviteMemberRequest): Promise<InviteMemberResponse> {
  const { data } = await apiClient.post<InviteMemberResponse>(
    `/organizations/${req.organizationId}/members/invite`,
    req,
  );
  return data;
}

export async function updateMemberRole(
  req: UpdateMemberRoleRequest,
): Promise<UpdateMemberRoleResponse> {
  const { data } = await apiClient.patch<UpdateMemberRoleResponse>(
    `/organizations/${req.organizationId}/members/${req.userId}`,
    req,
  );
  return data;
}

export async function removeMember(
  organizationId: string,
  userId: string,
  removedBy: string,
): Promise<RemoveMemberResponse> {
  const { data } = await apiClient.delete<RemoveMemberResponse>(
    `/organizations/${organizationId}/members/${userId}`,
    { params: { removed_by: removedBy } },
  );
  return data;
}

export const MEMBER_ROLE_OPTIONS: { value: MemberRole; label: string }[] = [
  { value: "MEMBER_ROLE_OWNER", label: "Owner" },
  { value: "MEMBER_ROLE_ADMIN", label: "Admin" },
  { value: "MEMBER_ROLE_MEMBER", label: "Member" },
  { value: "MEMBER_ROLE_VIEWER", label: "Viewer" },
];

export function memberRoleLabel(role: MemberRole): string {
  return MEMBER_ROLE_OPTIONS.find((o) => o.value === role)?.label ?? role;
}
